import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import OfflineIndicator from "./components/OfflineIndicator";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import VendingPage from "./pages/VendingPage";
import ProductsPage from "./pages/ProductsPage";
import OrdersPage from "./pages/OrdersPage";
import SuppliersPage from "./pages/SuppliersPage";
import RefillPage from "./pages/RefillPage";
import InventoryPage from "./pages/InventoryPage";
import CashPage from "./pages/CashPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import WishPage from "./pages/WishPage";
import ProblemPage from "./pages/ProblemPage";
import ImportPage from "./pages/ImportPage";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://snackomat.luenefire.de';
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("snack_auth") === "true";
  });

  const login = (password) => {
    return axios.post(`${API}/auth/login`, { password })
      .then(res => {
        if (res.data.success) {
          setIsAuthenticated(true);
          localStorage.setItem("snack_auth", "true");
          toast.success("Erfolgreich angemeldet");
          return true;
        }
        return false;
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || "Anmeldung fehlgeschlagen");
        return false;
      });
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("snack_auth");
    toast.info("Abgemeldet");
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
        <OfflineIndicator />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/wunsch" element={<WishPage />} />
          <Route path="/problem" element={<ProblemPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardPage />} />
            <Route path="automat" element={<VendingPage />} />
            <Route path="produkte" element={<ProductsPage />} />
            <Route path="bestellungen" element={<OrdersPage />} />
            <Route path="lieferanten" element={<SuppliersPage />} />
            <Route path="auffuellen" element={<RefillPage />} />
            <Route path="lager" element={<InventoryPage />} />
            <Route path="kasse" element={<CashPage />} />
            <Route path="berichte" element={<ReportsPage />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="einstellungen" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
